import { useSelector } from "react-redux";
import { Box, Stepper, Step, StepLabel } from "@mui/material";
import { Formik } from "formik";
import { useState } from "react";
import * as yup from "yup";
import Payment from "./Payment";
import Shipping from "./Shipping";
import { loadStripe } from "@stripe/stripe-js";
import CustomButton from "../../components/CustomButton";
import OrderSummary from "../../components/OrderSummary";
import { apiPost } from "../../apis/api";

const stripePromise = loadStripe("stripe_public_key");

const Checkout = () => {
  const [activeStep, setActiveStep] = useState(0);
  const cart = useSelector((state) => state.cart.cart);

  // Step flags
  const isFirstStep = activeStep === 0;
  const isSecondStep = activeStep === 1;
  const isThirdStep = activeStep === 2;

  const handleFormSubmit = async (values, actions, step) => {
    if (step === 0) {
      setActiveStep(1);
      if (values.shippingAddress.isSameAddress) {
        actions.setFieldValue("shippingAddress", {
          ...values.billingAddress,
          isSameAddress: true,
        });
      }
      actions.setTouched({});
    } else if (step === 1) {
      setActiveStep(2);
    } else if (step === 2) {
      // Stripe logic
      const items = cart.map((item) => ({
        name: item.Name || item.name,
        price: item.price,
        quantity: item.count || 1,
      }));
      const email = values.email;
      const data = await apiPost("/api/create-checkout-session", {
        items,
        email,
      });
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Payment error: " + (data.error || "Unknown error"));
      }
      return;
    }
  };

  return (
    <Box width="90%" maxWidth="1200px" m="100px auto">
      <Stepper activeStep={activeStep} sx={{ m: "20px 0" }}>
        <Step>
          <StepLabel>Billing</StepLabel>
        </Step>
        <Step>
          <StepLabel>Review Order</StepLabel>
        </Step>
        <Step>
          <StepLabel>Pay</StepLabel>
        </Step>
      </Stepper>
      <Box>
        <Formik
          onSubmit={(values, actions) =>
            handleFormSubmit(values, actions, activeStep)
          }
          initialValues={initialValues}
          validationSchema={checkoutSchema[activeStep === 2 ? 1 : activeStep]}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit}>
              {isFirstStep && (
                <Shipping
                  values={values}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                />
              )}
              {isSecondStep && (
                <>
                  <OrderSummary cart={cart} values={values} />
                  <Payment
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                </>
              )}
              {isThirdStep && (
                <>
                  <OrderSummary cart={cart} values={values} />
                  <Box textAlign="center" mt={4}>
                    <CustomButton
                      variant="contained"
                      size="large"
                      type="submit"
                      sx={{
                        minWidth: 220,
                        fontSize: "1.2rem",
                        fontWeight: 700,
                      }}
                    >
                      Pay
                    </CustomButton>
                  </Box>
                </>
              )}
              <Box display="flex" justifyContent="center" gap={2} mt={4}>
                {activeStep > 0 && (
                  <CustomButton
                    fullWidth
                    color="primary"
                    variant="contained"
                    onClick={() => setActiveStep(activeStep - 1)}
                    sx={{
                      minHeight: "40px",
                      padding: "8px 14px",
                      width: "500px",
                    }}
                  >
                    Back
                  </CustomButton>
                )}
                {activeStep < 2 && (
                  <CustomButton
                    fullWidth
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{
                      minHeight: "40px",
                      padding: "8px 14px",
                      width: "500px",
                    }}
                  >
                    {activeStep === 0 ? "Next" : "Pay"}
                  </CustomButton>
                )}
              </Box>
            </form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

const initialValues = {
  billingAddress: {
    firstName: "",
    lastName: "",
    country: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  shippingAddress: {
    isSameAddress: true,
    firstName: "",
    lastName: "",
    country: "",
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
  },
  email: "",
  phoneNumber: "",
};

const checkoutSchema = [
  yup.object().shape({
    billingAddress: yup.object().shape({
      firstName: yup.string().required("required"),
      lastName: yup.string().required("required"),
      country: yup.string().required("required"),
      street1: yup.string().required("required"),
      street2: yup.string(),
      city: yup.string().required("required"),
      state: yup.string().required("required"),
      zipCode: yup.string().required("required"),
    }),
    shippingAddress: yup.object().shape({
      isSameAddress: yup.boolean(),
      firstName: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
      lastName: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
      country: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
      street1: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
      street2: yup.string(),
      city: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
      state: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
      zipCode: yup.string().when("isSameAddress", {
        is: false,
        then: yup.string().required("required"),
        otherwise: yup.string(),
      }),
    }),
  }),
  yup.object().shape({
    email: yup.string().required("required"),
    phoneNumber: yup.string().required("required"),
  }),
];

export default Checkout;
